import { Injectable, inject, signal, computed, Signal, WritableSignal } from '@angular/core';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus, EventType, EventMessage, AccountInfo } from '@azure/msal-browser';
import { tap } from 'rxjs';
import { TokenClaims } from '@app/auth/models/token-claims.type';

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private msalService = inject(MsalService);
    private msalBroadcastService = inject(MsalBroadcastService);

    private _isAuthenticated: WritableSignal<boolean> = signal<boolean>(false);
    private _isAuthLoading: WritableSignal<boolean> = signal<boolean>(true);
    private _userAccount: WritableSignal<AccountInfo | null> = signal<AccountInfo | null>(null);
    private _userPhoto: WritableSignal<Blob | null> = signal<Blob | null>(null);
    private _isPhotoLoading: WritableSignal<boolean> = signal<boolean>(false);
    private _photoError: WritableSignal<string | null> = signal<string | null>(null);

    public isAuthenticated: Signal<boolean> = computed(() => this._isAuthenticated());
    public isAuthLoading: Signal<boolean> = computed(() => this._isAuthLoading());
    public userIdTokenClaims: Signal<TokenClaims | null> = computed(
        () => this._userAccount()?.idTokenClaims as TokenClaims
    );
    public userName: Signal<string | null> = computed(
        () => `${this.userIdTokenClaims()?.preferred_family_name}, ${this.userIdTokenClaims()?.preferred_given_name}`
    );
    private expirationTime: Date;

    constructor() {
        this.initializeAuth();
    }

    public logout(): void {
        this._userPhoto.set(null);
        this.msalService.logoutRedirect();
    }

    public getUserPhotoUrl(): string | null {
        const photo = this._userPhoto();
        return photo ? URL.createObjectURL(photo) : null;
    }

    private initializeAuth(): void {
        this.subscribeToLogin();
        this.subscribeToLogout();
    }

    private subscribeToLogin(): void {
        this.msalBroadcastService.inProgress$
            .pipe(
                tap((status: InteractionStatus) => {
                    this._isAuthLoading.set(status !== InteractionStatus.None);

                    if (status === InteractionStatus.None) {
                        this.processLoginDoneAndUpdateUserData();
                    }
                })
            )
            .subscribe();
    }

    private subscribeToLogout(): void {
        this.msalBroadcastService.msalSubject$
            .pipe(
                tap((msg: EventMessage) => {
                    if (msg.eventType === EventType.LOGOUT_SUCCESS) {
                        this._isAuthenticated.set(false);
                        this._userAccount.set(null);
                        this._userPhoto.set(null);
                    }
                })
            )
            .subscribe();
    }

    private processLoginDoneAndUpdateUserData(): void {
        const accounts = this.msalService.instance.getAllAccounts();

        if (accounts.length > 0) {
            const activeAccount = this.msalService.instance.getActiveAccount() || accounts[0];

            if (activeAccount && activeAccount.idTokenClaims?.roles?.length) {
                this.msalService.instance.setActiveAccount(activeAccount);
                this._userAccount.set(activeAccount);
                this._isAuthenticated.set(true);

                this.setExpirationTime(activeAccount.idToken);

                // Load user photo after successful authentication
                // this.loadUserPhoto();
            } else {
                this.setUnauthorizedUserSignals();
            }
        } else {
            this.setUnauthorizedUserSignals();
        }

        this._isAuthLoading.set(false);
    }

    private setUnauthorizedUserSignals() {
        this._isAuthenticated.set(false);
        this._userAccount.set(null);
        this._userPhoto.set(null);
    }

    get expirationTimeInMinutes(): number {
        return this.expirationTime ? this.expirationTime.differenceInMinute() : null;
    }

    private setExpirationTime(idToken: string = null): void {
        if (!idToken) {
            idToken = this.msalService.instance.getActiveAccount().idToken;
        }

        const decodedToken = JSON.parse(atob(idToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
        this.expirationTime = new Date(decodedToken.exp * 1000);
    }
}
